import { useState, useEffect, useCallback } from 'react';
import { TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  sendPushNotification,
  getRecentNotifications,
  deleteNotification,
  type SentNotification,
} from '@/services/adminService';
import { formatRelativeTime } from '@/utils/dates';

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent' }
  | { kind: 'error'; message: string };

/** Turns a callable's error into something an admin can act on. */
function describeError(err: unknown): string {
  const code = (err as { code?: string })?.code ?? '';
  const message = (err as { message?: string })?.message ?? String(err);

  if (code.includes('unauthenticated')) return 'Your session expired. Sign in again.';
  if (code.includes('permission-denied'))
    return 'This account is not an admin, so it cannot send notifications.';
  if (code.includes('invalid-argument')) return 'Title and body are both required.';
  if (code.includes('not-found') || code.includes('internal'))
    return `Could not reach the notification service. ${message}`;
  return message;
}

export default function NotificationPage() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [topic, setTopic] = useState('announcements');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [recent, setRecent] = useState<SentNotification[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<SentNotification | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleDelete(n: SentNotification) {
    setDeletingId(n.id);
    setDeleteError(null);
    try {
      await deleteNotification(n.id);
      setRecent((prev) => prev.filter((r) => r.id !== n.id));
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(
        `Could not delete. ${(err as Error).message || 'Check your connection and try again.'}`
      );
    } finally {
      setDeletingId(null);
    }
  }

  const loadRecent = useCallback(async () => {
    try {
      setRecent(await getRecentNotifications(10));
    } catch {
      // History is non-critical; a failure here must not hide the compose form.
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const canSend = title.trim().length > 0 && body.trim().length > 0 && status.kind !== 'sending';

  async function handleSend() {
    if (!canSend) return;
    setStatus({ kind: 'sending' });
    try {
      await sendPushNotification({ title: title.trim(), body: body.trim(), topic });
      setStatus({ kind: 'sent' });
      setTitle('');
      setBody('');
      loadRecent();
    } catch (err) {
      setStatus({ kind: 'error', message: describeError(err) });
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        <p className="text-sm text-gray-500 mt-1">
          Send push notifications to app users
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compose */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Compose Notification
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Notification title..."
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-saffron-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Body
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Notification message..."
                rows={4}
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-saffron-500 focus:border-transparent resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Audience
              </label>
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none"
              >
                <option value="announcements">All Users (Announcements)</option>
                <option value="daily-verse">Daily Verse Subscribers</option>
              </select>
            </div>

            <button
              onClick={handleSend}
              disabled={!canSend}
              className="w-full py-2.5 bg-saffron-500 text-white rounded-lg text-sm font-medium hover:bg-saffron-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status.kind === 'sending' ? 'Sending…' : 'Send Notification'}
            </button>

            {status.kind === 'sent' && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                Notification sent.
              </p>
            )}
            {status.kind === 'error' && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {status.message}
              </p>
            )}
          </div>
        </div>

        {/* Preview */}
        <div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Preview
            </h3>
            <div className="bg-gray-100 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-saffron-500 flex items-center justify-center text-white text-lg flex-shrink-0">
                  🙏
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {title || 'Notification Title'}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {body || 'Notification body will appear here...'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">now</p>
                </div>
              </div>
            </div>
          </div>

          {/* History */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Recent Notifications
            </h3>
            {loadingRecent ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : recent.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No notifications sent yet
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recent.map((n) => (
                  <div key={n.id} className="py-3 group">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-gray-900">{n.title}</p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400">
                          {formatRelativeTime(n.sentAt)}
                        </span>
                        <button
                          onClick={() => setConfirmDelete(n)}
                          disabled={deletingId === n.id}
                          title="Delete from history"
                          aria-label={`Delete notification “${n.title}”`}
                          className="p-1 rounded text-gray-300 hover:text-red-600 hover:bg-red-50 focus:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-400 opacity-0 group-hover:opacity-100 transition disabled:opacity-40"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deletion confirm. Worth a step: this also removes the entry from every
          user's in-app notification list, which is not obvious from "delete". */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !deletingId && setConfirmDelete(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900">Delete this notification?</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  “{confirmDelete.title}”
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              This removes it from the history and from every user&rsquo;s in-app
              notification list. Pushes already delivered to a device are not
              affected, and this cannot be undone.
            </p>

            {deleteError && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">
                {deleteError}
              </p>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={!!deletingId}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={!!deletingId}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60"
              >
                {deletingId ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
